import * as React from "react";
import { useNavigate, useNavigationType } from "react-router-dom";
import AnimatedCoverTabs from "../../../components/atoms/tab/AnimatedConverTab";
import AppBar from "../../../components/atoms/appbar";
import Button from "../../../components/atoms/button";
import { vw } from "../../../lib/utils/responsiveUtils";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useGetResveCancelList, useGetResveList } from "../../../@apis/hooks/useResve";
import { formatKoDayDate } from "../../../lib/utils/dateUtils";
import { staticURL } from "../../../lib/utils/imageUtils";
import { cn } from "../../../lib/utils/cn";

const TAB_LABELS = [
  { label: "예약/결제", value: "1" },
  { label: "라운드 완료", value: "2" },
  { label: "취소/환불", value: "3" },
];

const Z_CLASSES = ["z-40", "z-30", "z-20", "z-10"] as const;

export default function ReservationHistory() {
  const navigate = useNavigate();
  const navigationType = useNavigationType(); 
  const queryClient = useQueryClient();

  const [tab, setTab] = React.useState<string>(() => {
    // 뒤로가기(POP)로 들어온 경우: 저장된 탭이 있으면 그걸 사용
    if (navigationType === "POP") {
      const saved = sessionStorage.getItem("reservationHistoryTab");
      // 없으면 기본값 "1"
      return saved ?? "1";
    }

    // PUSH / REPLACE 인 경우: 새로 들어온 걸로 보고 항상 1로 초기화
    sessionStorage.removeItem("reservationHistoryTab"); // 혹시 남아있던 값 제거
    return "1";
  });

  const isCompleteRound = tab === "2";
  const isCancelTab = tab === "3"

  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (tab === "1" || tab === "2") {
      queryClient.removeQueries({ queryKey: ["resve-cancel-list"], exact: false });
    }

    if (tab === "3") {
      queryClient.removeQueries({ queryKey: ["resve-list"], exact: false });
    }

    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [tab, queryClient]);

  // 예약 조회
  const { data: resvePages, hasNextPage: hasNextResve, fetchNextPage: fetchNextResve, isFetchingNextPage: isFetchingResve } = useInfiniteQuery({
    ...useGetResveList({ isCompleteRound }),
    enabled: !isCancelTab,
  });
  const resveList = React.useMemo(
    () =>
      (resvePages?.pages ?? []).flatMap(
        (p) => p.data?.resveList ?? []
      ),
    [resvePages]
  );

  // 예약 취소 조회
  const { data: cancelPages, hasNextPage: hasNextCancel, fetchNextPage: fetchNextCancel, isFetchingNextPage: isFetchingCancel } = useInfiniteQuery({
    ...useGetResveCancelList({}),
    enabled: isCancelTab,
  });
  const cancelList = React.useMemo(
    () =>
      (cancelPages?.pages ?? []).flatMap(
        (p) => p.data?.myResveCancelList ?? []
      ),
    [cancelPages]
  );

  const triggerRef = React.useRef(null);

  React.useEffect(() => {
    const el = triggerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;

        if (!isCancelTab) {
          if (hasNextResve && !isFetchingResve) fetchNextResve();
        } else {
          if (hasNextCancel && !isFetchingCancel) fetchNextCancel();
        }
      },
      { root: document.querySelector('main'), threshold: 0 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [isCancelTab, hasNextResve, isFetchingResve, fetchNextResve, hasNextCancel, isFetchingCancel, fetchNextCancel]);

  const groupByDate = <T extends { baseDate?: string }>(list: T[]) => {
    const map = new Map<string, T[]>();

    for (const item of list) {
      const key = item.baseDate ?? "NO_DATE";

      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(item);
    }

    return Array.from(map.entries()); 
  };

  const groupedResve = groupByDate(resveList);
  const groupedCancel = groupByDate(cancelList);

  return (
    <div ref={containerRef} className="overflow-y-auto h-full relative flex flex-col">
      <AppBar title="골프장 예약/결제 내역" backButton />

      <div className="px-[15px] flex-1 flex flex-col">
        <AnimatedCoverTabs
          tabs={TAB_LABELS}
          value={tab}
          onChange={(value) => {
            setTab(value);                           
            sessionStorage.setItem("reservationHistoryTab", value); // 세션에도 저장
          }}
        />

        {/* 취소/환불 탭 */}
        {tab === "3" ? (
          groupedCancel.length > 0 ? (
            <div className="space-y-[35px]">
              {groupedCancel.map(([baseDate, items]) => (
                <section key={baseDate}>
                  <div className="mt-[20px] mb-[19px] font-bold text-[#222]" style={{ fontSize: vw(20) }}>
                    {formatKoDayDate(baseDate)}
                  </div>

                  {items.map((item) => {
                    const isLast = item.id === cancelList.at(-1)?.id;

                    return (
                      <div 
                        key={item.id} 
                        ref={isLast ? triggerRef : undefined}
                        onClick={() => navigate(`/setting/reservation-history-cancel/${item.id}`)} 
                        className="mb-[10px]"
                      >
                        <div className="rounded-[15px] bg-[#f5f5f5] p-[20px] flex items-center justify-between" style={{ height: vw(60) }}>
                          <p className="font-bold text-[#ccc] line-through" style={{ fontSize: vw(16) }}>
                            {item.startTime}
                          </p>
                          <div className="flex items-center gap-[10px]">
                            <p className="text-[#222] font-bold" style={{ fontSize: vw(16) }}>
                              예약 취소
                            </p>
                            <img
                              src="/icons/ic-go.webp"
                              style={{ width: vw(6), height: vw(14) }}
                            />
                          </div>
                        </div>
  
                        <div className="mx-auto" style={{ width: vw(315), height: "1px", backgroundImage: "repeating-linear-gradient(to right,#e6e6e6 0 3px,transparent 6px 5px)", borderRadius: "2px" }} />
  
                        <div className="rounded-[15px] bg-[#f5f5f5] p-[20px] flex items-center" style={{ height: vw(124) }}>
                          <div className="flex gap-[19px] items-center">
                            <img
                              className="rounded-[10px] object-cover bg-white"
                              style={{ width: vw(83), height: vw(83) }}
                              src={item.golfParkImageUrl ? staticURL(item.golfParkImageUrl) : "/images/img-login-logo.webp"}
                              alt="골프장 이미지"
                            />
                            <div className="flex flex-col justify-start">
                              <p className="text-[#222] font-bold line-clamp-1" style={{ fontSize: vw(20) }}>
                                {item.golfParkName}
                              </p>
                              <div className="mt-[18px] pl-[15px] border-l-[2px] border-[#e6e6e6]">
                                <p className="text-[14px] text-[#333] font-[500]" style={{ fontSize: vw(14) }}>
                                  {item.golfCourseName}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </section>
              ))}
            </div>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center pb-[80px] gap-[14px]">
              <img src="/icons/ic-notice.webp" />
              <p className="font-[500] text-center text-[#ccc] leading-[1.5]" style={{ fontSize: vw(14) }}>
                취소/환불 내역이 없습니다.
              </p>
            </div>
          )
        ) : groupedResve.length > 0 ? (
          <div className="space-y-[35px]">
            {groupedResve.map(([baseDate, items]) => (
              <section key={baseDate}>
                <div className="mt-[20px] mb-[19px] font-bold text-[#222]" style={{ fontSize: vw(20) }}>
                  {formatKoDayDate(baseDate)}
                </div>

                {items.map((item) => {
                  const isLast = item.resveSeq === resveList.at(-1)?.resveSeq;

                  return (
                    <div 
                      key={item.resveSeq} 
                      ref={isLast ? triggerRef : undefined}
                      onClick={() => navigate(`/setting/reservation-history/${item.resveSeq}`)} 
                      className="mb-[10px]"
                    >
                      <div className="rounded-[15px] bg-[#f5f5f5] p-[20px] flex items-center justify-between" style={{ height: vw(60) }}>
                        <p className="font-bold text-[#222]" style={{ fontSize: vw(16) }}>
                          {item.startTime}
                        </p>
                        <div className="flex items-center gap-[10px]">
                          <p className="text-[#222] font-bold" style={{ fontSize: vw(16) }}>
                            예약 완료
                          </p>
                          <img
                            src="/icons/ic-go.webp"
                            style={{ width: vw(6), height: vw(14) }}
                          />
                        </div>
                      </div>
  
                      <div className="mx-auto" style={{ width: vw(315), height: "1px", backgroundImage: "repeating-linear-gradient(to right,#e6e6e6 0 3px,transparent 6px 5px)", borderRadius: "2px" }} />
  
                      <div className="rounded-[15px] bg-[#f5f5f5] p-[20px] flex items-center"  style={{ height: vw(142) }}>
                        <div className="flex gap-[19px] items-start">
                          {tab === "2" ? (
                            <div className="relative shrink-0">
                              <img
                                src={item.golfParkImageUrl ? staticURL(item.golfParkImageUrl) : "/images/img-login-logo.webp"}
                                alt="골프장 이미지"
                                className="rounded-[10px] brightness-50 object-cover"
                                style={{ width: vw(83), height: vw(83) }}
                              />
                              <img
                                src="/icons/ic-stamp.webp"
                                alt="완료"
                                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                                style={{ width: vw(30), height: vw(35) }}
                              />
                            </div>
                          ) : (
                            <img
                              className="rounded-[10px] object-cover"
                              style={{ width: vw(83), height: vw(83) }}
                              src={item.golfParkImageUrl ? staticURL(item.golfParkImageUrl) : "/images/img-login-logo.webp"}
                              alt="골프장 이미지"
                            />
                          )}
  
                          <div className="flex flex-col justify-start">
                            <p className="text-[#222] font-bold line-clamp-1" style={{ fontSize: vw(20) }}>
                              {item.golfParkName}
                            </p>
                            <div className="mt-[18px] pl-[15px] border-l-[2px] border-[#e6e6e6]">
                              <p className="text-[14px] text-[#333] font-[500]" style={{ fontSize: vw(14) }}>
                                {item.golfCourseName}
                              </p>
  
                              <div className="mt-[9px] flex items-center -space-x-[10px]">
                                {[0, 1, 2, 3].map((_, i) => {
                                  const m = item.resveTimeMemberList?.[i] || {};                                
                                  const myMember = item.resveTimeMemberList?.[0];
  
                                  const bg = m.sex === undefined ? "#e6e6e6" : m.sex === null ? "#e6e6e6" : m.sex === 0 ? "#f95e80" : "#25a1d5";
  
                                  const icon =
                                    m.sex === undefined
                                      ? "/icons/ic-rest.webp"
                                      : m.sex === null
                                      ? "/icons/ic-gender.webp"
                                      : m.sex === 0
                                      ? "/icons/ic-female.webp"
                                      : "/icons/ic-male.webp";
  
                                  return (
                                    <div
                                      key={i}
                                      className={`${Z_CLASSES[i]} rounded-full flex justify-center items-center border-2 ${
                                        !!myMember?.groupUuid && m.groupUuid === myMember?.groupUuid ? "border-[#222]" : "border-white"
                                      }`}
                                      style={{ width: vw(40), height: vw(40), backgroundColor: bg }}
                                    >
                                      <img src={icon} style={{ width: vw(26), height: vw(24) }} alt="플레이어" />
                                    </div>
                                  ); 
                                })}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </section>
            ))}
          </div>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center pb-[80px] gap-[14px]">
            <img src="/icons/ic-notice.webp" />
            <p className="font-[500] text-center text-[#ccc] leading-[1.5]" style={{ fontSize: vw(14) }}>
              {tab === "1" ? "예약/결제" : "라운드"} 내역이 없습니다.<br />
              <span className="text-[#222] underline underline-offset-[3px]">예약하기</span>를 눌러 파크골프를 즐겨보세요.
            </p>

            <div className={cn("fixed bottom-0 bg-white p-[15px] w-full max-w-[440px] gap-[10px] z-50")}>
              <Button
                type="button"
                className="w-full font-bold h-[50px]"
                variant={"red"}
                label="예약하기"
                onClick={() => navigate("/reservation")}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
